import { Alert, AlertTitle, Box, Stack, Typography } from '@mui/material';
import { CtaButton } from '@tassomutuo-backoffice/ui';

import {
  ACQUISITION_STEPS,
  ACQUISITION_STEP_LABEL,
  ACQUISITION_STEP_STATE_LABEL,
} from './acquisitionOutcome';
import { ACQUISITION_RECOMPILE_MESSAGE } from './useAcquisitionPanel';
import type { AcquisitionRecoveryState } from './useAcquisitionPanel';

interface IProps {
  /** How this product is named to the operator, for the operation's identity. */
  productLabel: string;
  /** What the incomplete result said, in the operator's words. */
  notice: string | null;
  recovery: AcquisitionRecoveryState;
}

/**
 * What an operator sees and can do about durable work that did not finish.
 *
 * It reads as unfinished work rather than as a failure or a success, and it
 * shows only what identifies the operation — its steps, its stable code, its
 * product and its request ID. Everything the customer told the operator stays
 * out of it: support investigates from the request ID, never from the payload.
 *
 * Exactly one way forward is offered, because an incomplete operation has
 * exactly one: continue it where the backend already wrote something, resend
 * the unchanged command when nothing was written and the browser still holds
 * it, or start over when neither is true.
 */
const AcquisitionRecoveryPanel = ({
  productLabel,
  notice,
  recovery,
}: IProps) => {
  const { operation, message, mode } = recovery;

  // A resolution attempt can end the operation — a retry that completed it, or
  // one the backend no longer knows. What it had to say outlives it.
  if (!operation) {
    return message ? (
      <Alert severity="info" sx={{ mb: 3 }}>
        {message}
      </Alert>
    ) : null;
  }

  return (
    <Alert severity="warning" role="status" sx={{ mb: 3 }}>
      <AlertTitle sx={{ fontWeight: 700 }}>{notice}</AlertTitle>

      <Stack spacing={0.5}>
        {ACQUISITION_STEPS.map((step) => (
          <Typography component="p" variant="body2" key={step}>
            {ACQUISITION_STEP_LABEL[step]}:{' '}
            <Box component="span" fontWeight={600}>
              {ACQUISITION_STEP_STATE_LABEL[operation.steps[step]]}
            </Box>
          </Typography>
        ))}
      </Stack>

      {/* What support needs to find the operation, and nothing the customer
          said: identifiers are set apart so they can be read out or copied. */}
      <Box
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: 1,
          borderColor: 'divider',
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          columnGap: 3,
          rowGap: 0.5,
        }}
      >
        <Typography component="p" variant="body2">
          Prodotto: {productLabel}
        </Typography>
        <Typography component="p" variant="body2">
          Codice:{' '}
          <Box component="span" fontFamily="monospace">
            {operation.code ?? 'internal_error'}
          </Box>
        </Typography>
        <Typography component="p" variant="body2">
          Operazione:{' '}
          <Box component="span" fontFamily="monospace">
            {operation.operationId}
          </Box>
        </Typography>
        <Typography component="p" variant="body2">
          ID richiesta:{' '}
          <Box component="span" fontFamily="monospace">
            {operation.requestId}
          </Box>
        </Typography>
      </Box>

      {mode === 'recompile' && (
        <Typography component="p" variant="body2" sx={{ mt: 2 }}>
          {ACQUISITION_RECOMPILE_MESSAGE}
        </Typography>
      )}

      {message && (
        <Alert
          severity={recovery.leaseHeld ? 'info' : 'error'}
          sx={{ mt: 2 }}
          role={recovery.leaseHeld ? 'status' : undefined}
        >
          {message}
        </Alert>
      )}

      <Stack direction="row" spacing={2} sx={{ mt: 2 }} flexWrap="wrap">
        {mode === 'retry' && (
          <CtaButton
            variant="contained"
            onClick={recovery.retry}
            loading={recovery.isBusy}
            // A held lease is the backend already doing this work: asking again
            // inside it can only be answered with the same transient refusal.
            disabled={recovery.isBusy || recovery.leaseHeld}
          >
            Riprova completamento
          </CtaButton>
        )}
        {mode === 'resubmit' && (
          <CtaButton
            variant="contained"
            onClick={recovery.resubmit}
            loading={recovery.isBusy}
            disabled={recovery.isBusy}
          >
            Invia di nuovo la richiesta
          </CtaButton>
        )}
        {mode === 'recompile' && (
          <CtaButton
            variant="contained"
            onClick={recovery.recompile}
            disabled={recovery.isBusy}
          >
            Ricompila la richiesta
          </CtaButton>
        )}
        {mode !== 'recompile' && (
          <CtaButton
            variant="outlined"
            onClick={recovery.refreshStatus}
            disabled={recovery.isBusy}
          >
            Aggiorna stato
          </CtaButton>
        )}
      </Stack>
    </Alert>
  );
};

export default AcquisitionRecoveryPanel;
